import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class Commerce7Order extends ModelObject<Commerce7Order> {

    public version: number = 0;
    /**
    * The Commerce7 unique identifier for this order.
    */
    public commerce7OrderId: Guid = Guid.createEmpty();
    /**
    * Order number in Commerce7
    */
    public commerce7OrderNumber: number = 0;
    /**
    * The date and time this order was created in Commerce7.
    */
    public createdAt: Instant = Instant.ofEpochMilli(0);
    /**
    * The total number of items in the order.
    */
    public totalOrderQuantity: number = 0;
    /**
    * The total net value of the order including delivery.
    */
    public totalNetPrice: number = 0;
    private _brewmanOrderId: Guid | null = null;
    /**
    * The Brewman order id assigned to this order on a successful import. If empty, check the error message field for details.
    */
    public get brewmanOrderId(): Guid | null { return this._brewmanOrderId }
    public set brewmanOrderId(value: Guid | null) {
      this._brewmanOrderId = value === undefined ? null : value;
    }
    /**
    * Error information associated with a failed import.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public errorMessage: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.commerce7OrderId = Guid.parseOrEmpty(jsonSerializedObject.commerce7OrderId);
          this.commerce7OrderNumber = jsonSerializedObject.commerce7OrderNumber;
          this.createdAt = Instant.parse(jsonSerializedObject.createdAt);
          this.totalOrderQuantity = jsonSerializedObject.totalOrderQuantity;
          this.totalNetPrice = jsonSerializedObject.totalNetPrice;
          this.brewmanOrderId = jsonSerializedObject.brewmanOrderId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.brewmanOrderId);
          this.errorMessage = jsonSerializedObject.errorMessage;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.commerce7OrderId = this.commerce7OrderId.toString();
        toRet.commerce7OrderNumber = this.commerce7OrderNumber;
        toRet.createdAt = this.createdAt.toString();
        toRet.totalOrderQuantity = this.totalOrderQuantity;
        toRet.totalNetPrice = this.totalNetPrice;
        toRet.brewmanOrderId = this.brewmanOrderId == null ? null : this.brewmanOrderId.toString();
        toRet.errorMessage = this.errorMessage;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "commerce7OrderId", rule: (v: any) => !v.isEmpty() || "Commerce7Order.Commerce7OrderId cannot be empty"},
            {field: "commerce7OrderNumber", rule: (v: any) => v == null || v >= -2147483648 || "Commerce7OrderNumber is not greater than minimum allowed value: -2147483648"},
            {field: "commerce7OrderNumber", rule: (v: any) => v == null || v <= 2147483647 || "Commerce7OrderNumber is above maximum allowed value: 2147483647"},
            {field: "brewmanOrderId", rule: (v: any) => v == null || !v.isEmpty() || "Commerce7Order.BrewmanOrderId cannot be empty"},
    ];
}


export class FilteredCommerce7OrdersResult extends ModelObject<FilteredCommerce7OrdersResult> {

    public matchingCount: number = 0;
    public commerce7Orders: (Commerce7Order)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.commerce7Orders = jsonSerializedObject.commerce7Orders.map((v: any) => new Commerce7Order(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.commerce7Orders = this.commerce7Orders.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "commerce7Orders", rule: (v: any) => !!v || "commerce7Orders is required"},
            {field: "commerce7Orders", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as Commerce7Order[]).reduce((acc: Array<string>, curr: Commerce7Order) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class Commerce7OrderFilter extends ModelObject<Commerce7OrderFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _createdFromDate: LocalDate | null = null;
    /**
    * Minimum (inclusive) created at date
    */
    public get createdFromDate(): LocalDate | null { return this._createdFromDate }
    public set createdFromDate(value: LocalDate | null) {
      this._createdFromDate = value === undefined ? null : value;
    }
    private _createdToDate: LocalDate | null = null;
    /**
    * Maximum (inclusive) created at date
    */
    public get createdToDate(): LocalDate | null { return this._createdToDate }
    public set createdToDate(value: LocalDate | null) {
      this._createdToDate = value === undefined ? null : value;
    }
    private _commerce7OrderIds: readonly (Guid)[] | null = null;
    /**
    * Limit to specified Commerce7 order ids
    */
    public get commerce7OrderIds(): readonly (Guid)[] | null { return this._commerce7OrderIds }
    public set commerce7OrderIds(value: readonly (Guid)[] | null) {
      this._commerce7OrderIds = value === undefined ? null : value;
    }
    private _commerce7OrderNumbers: readonly (string)[] | null = null;
    /**
    * Limit to specified Commerce7 order numbers
    */
    public get commerce7OrderNumbers(): readonly (string)[] | null { return this._commerce7OrderNumbers }
    public set commerce7OrderNumbers(value: readonly (string)[] | null) {
      this._commerce7OrderNumbers = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.createdFromDate = jsonSerializedObject.createdFromDate == null ? null : LocalDate.parse(jsonSerializedObject.createdFromDate);
          this.createdToDate = jsonSerializedObject.createdToDate == null ? null : LocalDate.parse(jsonSerializedObject.createdToDate);
          this.commerce7OrderIds = jsonSerializedObject.commerce7OrderIds == null ? null : jsonSerializedObject.commerce7OrderIds.map((v: any) => Guid.parseOrEmpty(v));
          this.commerce7OrderNumbers = jsonSerializedObject.commerce7OrderNumbers == null ? null : jsonSerializedObject.commerce7OrderNumbers.map((v: any) => v);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.createdFromDate = this.createdFromDate == null ? null : this.createdFromDate.toString();
        toRet.createdToDate = this.createdToDate == null ? null : this.createdToDate.toString();
        toRet.commerce7OrderIds = this.commerce7OrderIds == null ? null : this.commerce7OrderIds.map(v => v.toString());
        toRet.commerce7OrderNumbers = this.commerce7OrderNumbers == null ? null : this.commerce7OrderNumbers.map(v => v);
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
    ];
}


export class Commerce7OrderService extends ServiceBase {

  public async getCommerce7OrdersByFilter(filter: Commerce7OrderFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredCommerce7OrdersResult> {
    const req = {
    tenantId:api.tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredCommerce7OrdersResult(response.results);;
    return this.makeJsonRequest("commerce7Order/v1/getCommerce7OrdersByFilter", req, false, extractor, opts);
  };
}

export const commerce7OrderService = new Commerce7OrderService();